import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { OwnedBanner } from '@/components/OwnedBanner'
import { OwnedChip, isConnectionOwned, type Owner } from '@/components/OwnedChip'
import { useViewOnly } from '@/components/templates/app-shell'
import { DetailPage } from '@/components/templates/detail-page'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CourseDeletionDialog } from '@/pages/CourseDeletionDialog'
import { CourseRoster } from '@/components/courses/CourseRoster'
import { EnrolmentDialog } from '@/components/courses/EnrolmentDialog'
import { EnrolmentDeletionDialog } from '@/components/courses/EnrolmentDeletionDialog'
import type { Enrolment } from '@/components/courses/enrolment'
import { deleteJSON, getJSON, patchJSON } from '@/lib/api'

interface CourseDetail {
  id: number
  title: string
  description: string | null
  delivered_by: string | null
  link: string | null
  credits: string
  credit_unit_label: string
  status: 'active' | 'archived'
  owner: Owner | null
  external_url: string | null
  last_synced_at: string | null
  total_enrolment_count: number
}

export interface CourseResponse {
  course: CourseDetail
}

interface CoursePageProps {
  courseId: number
  onBack: () => void
  onEdit: () => void
}

export function CourseDetailPage(props: CoursePageProps) {
  return <CourseRecord key={props.courseId} {...props} />
}

function CourseRecord({ courseId, onBack, onEdit }: CoursePageProps) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [dialog, setDialog] = useState<'add' | Enrolment | null>(null)
  const [deleteEnrolment, setDeleteEnrolment] = useState<Enrolment | null>(null)
  const [reveal, setReveal] = useState({ id: 0, version: 0 })
  const client = useQueryClient()
  const viewOnly = useViewOnly()
  const queryKey = ['course', courseId]
  const query = useQuery({
    queryKey,
    queryFn: () => getJSON<CourseResponse>(`/api/v1/courses/${courseId}`),
  })
  const lifecycle = useMutation({
    mutationFn: (action: 'archive' | 'unarchive') =>
      patchJSON<CourseResponse>(`/api/v1/courses/${courseId}/${action}`, {}),
    onSuccess: (response) => {
      client.setQueryData(queryKey, response)
      void client.invalidateQueries({ queryKey: ['courses'] })
    },
  })
  const deletion = useMutation({
    mutationFn: () => deleteJSON(`/api/v1/courses/${courseId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['courses'] })
      onBack()
    },
  })
  if (query.isPending)
    return <p className="p-6 text-body-sm text-muted-foreground">Loading course...</p>
  if (query.isError) return <Alert>Course unavailable</Alert>
  const course = query.data.course
  const archived = course.status === 'archived'
  const writable = !viewOnly && !isConnectionOwned(course.owner)
  return (
    <DetailPage
      title={course.title}
      backHref="/courses"
      backLabel="Courses"
      onBack={onBack}
      badge={
        <>
          <Badge variant={archived ? 'neutral' : 'success'}>
            {archived ? 'Archived' : 'Active'}
          </Badge>
          <OwnedChip owner={course.owner} />
        </>
      }
      actions={
        writable && (
          <CourseActions
            course={course}
            pending={lifecycle.isPending}
            onEdit={onEdit}
            onArchive={() => lifecycle.mutate(archived ? 'unarchive' : 'archive')}
            onDelete={() => {
              deletion.reset()
              setConfirmDelete(true)
            }}
            onAdd={() => setDialog('add')}
          />
        )
      }
    >
      <CourseDeletionDialog
        open={confirmDelete}
        title={course.title}
        pending={deletion.isPending}
        error={deletion.error?.message ?? null}
        onClose={() => setConfirmDelete(false)}
        onDelete={() => deletion.mutate()}
      />
      {dialog && (
        <EnrolmentDialog
          courseId={courseId}
          courseTitle={course.title}
          unit={course.credit_unit_label}
          enrolment={dialog === 'add' ? undefined : dialog}
          onClose={() => setDialog(null)}
          onExisting={(id) => {
            setDialog(null)
            setReveal((current) => ({ id, version: current.version + 1 }))
          }}
        />
      )}
      {deleteEnrolment && (
        <EnrolmentDeletionDialog
          enrolment={deleteEnrolment}
          onClose={() => setDeleteEnrolment(null)}
        />
      )}
      <div className="mb-6 max-w-2xl space-y-4">
        <OwnedBanner record={course} />
        {lifecycle.isError && <Alert>{lifecycle.error.message}</Alert>}
        <CourseDetails course={course} />
      </div>
      <CourseRoster
        key={reveal.version}
        focusEnrolmentId={reveal.id || undefined}
        courseId={courseId}
        unit={course.credit_unit_label}
        onEdit={setDialog}
        onDelete={setDeleteEnrolment}
      />
    </DetailPage>
  )
}

function CourseActions({
  course,
  pending,
  onEdit,
  onArchive,
  onDelete,
  onAdd,
}: {
  course: CourseDetail
  pending: boolean
  onEdit: () => void
  onArchive: () => void
  onDelete: () => void
  onAdd: () => void
}) {
  const archived = course.status === 'archived'
  return (
    <>
      <Button variant="outline" onClick={onEdit}>
        Edit
      </Button>
      <Button variant="outline" disabled={pending} onClick={onArchive}>
        {archived ? 'Unarchive' : 'Archive'}
      </Button>
      {course.total_enrolment_count === 0 && (
        <Button variant="destructive" onClick={onDelete}>
          Delete
        </Button>
      )}
      {!archived && <Button onClick={onAdd}>Add enrolment</Button>}
    </>
  )
}

function CourseDetails({ course }: { course: CourseDetail }) {
  const details = [
    ['Description', course.description],
    ['Delivered by', course.delivered_by],
    ['Link', course.link],
    ['Credits', `${course.credits} ${course.credit_unit_label}`],
  ]
  return (
    <section aria-labelledby="course-details-heading">
      <h2 id="course-details-heading" className="mb-3 text-body font-semibold">
        Details
      </h2>
      <dl className="divide-y divide-border rounded-md border border-border">
        {details
          .filter(([, value]) => value?.trim())
          .map(([label, value]) => (
            <div key={label} className="flex gap-4 px-4 py-2 text-body-sm">
              <dt className="w-32 shrink-0 text-muted-foreground">{label}</dt>
              <dd className="min-w-0 whitespace-pre-wrap break-words">
                {label === 'Link' ? (
                  <a
                    href={value!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline"
                  >
                    {value}
                  </a>
                ) : (
                  value
                )}
              </dd>
            </div>
          ))}
      </dl>
    </section>
  )
}
